import { useContext, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { AuthContext } from '../context/AuthContext';
import { ActividadIndex } from '../interfaces/ActividadIndexInterface';
import ServiceNotification from '../components/Notifications/ServiceNotification';
import { API } from '../global/global';

interface NotificationData {
  actividadId: number;
  clienteNombre: string;
  tipoServicio: string;
  fecha: string;
  domicilio: string;
}

// Constantes
const CHECK_INTERVAL = 60000; // 1 minuto
const NOTIFICATION_WINDOW = { min: 14, max: 16 }; // minutos
const CLEANUP_THRESHOLD = 60 * 60 * 1000; // 1 hora
const TOAST_DURATION = 600000; // 10 minutos

/**
 * Custom Hook para manejar notificaciones automáticas de servicios programados
 * Verifica cada minuto si hay servicios próximos (15 min antes) para el usuario actual
 */
const useServiceNotifications = () => {
  const { token, usuario, isLoggedIn } = useContext(AuthContext);
  const notifiedServices = useRef<Set<number>>(new Set());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Función para obtener servicios programados creados por el usuario actual
  const fetchUserScheduledServices = useCallback(async (): Promise<ActividadIndex[]> => {
    if (!token || !usuario?.id) return [];

    try {
      const response = await fetch(`${API}/actividad?includeAll=true&ultima_etapa[eq]=2`, {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const { data } = await response.json();
      
      // Filtrar servicios creados por el usuario actual (etapa tipo_etapa_id === 1)
      return data?.filter((actividad: ActividadIndex) => {
        const etapaCreacion = actividad.etapa?.find(etapa => etapa.tipo_etapa_id === 1);
        return etapaCreacion?.usuario.id === usuario.id;
      }) || [];
    } catch (error) {
      console.error('Error fetching scheduled services:', error);
      return [];
    }
  }, [token, usuario?.id]);

  // Función para verificar si un servicio debe notificarse
  const shouldNotify = useCallback((fechaServicio: string): boolean => {
    const now = Date.now();
    const serviceTime = new Date(fechaServicio).getTime();
    const minutesDiff = Math.floor((serviceTime - now) / (1000 * 60));
    
    return minutesDiff >= NOTIFICATION_WINDOW.min && minutesDiff <= NOTIFICATION_WINDOW.max;
  }, []);

  // Función para obtener información del cliente/sucursal
  const getClientInfo = useCallback((actividad: ActividadIndex) => {
    const { servicio, usuario: actividadUsuario } = actividad;
    const tecnico = `${actividadUsuario.nombre || ''} ${actividadUsuario.apellido_materno || ''}`.trim();
    
    if (servicio?.sucursal?.nombre) {
      return {
        clienteNombre: tecnico,
        domicilio: servicio.sucursal.domicilio || servicio.domicilio_sucursal || 'Domicilio no especificado'
      };
    }
    
    if (servicio?.cliente) {
      return {
        clienteNombre: tecnico || servicio.cliente.razon_social || 'Cliente sin nombre',
        domicilio: servicio.domicilio_cliente || servicio.domicilio_sucursal || 'Domicilio no especificado'
      };
    }
    
    return {
      clienteNombre: 'Cliente sin nombre',
      domicilio: 'Domicilio no especificado'
    };
  }, []);

  // Función para mostrar la notificación
  const showServiceNotification = useCallback((notification: NotificationData) => {
    toast.info(
      <ServiceNotification
        clienteNombre={notification.clienteNombre}
        tipoServicio={notification.tipoServicio}
        fecha={notification.fecha}
        domicilio={notification.domicilio}
      />,
      {
        position: "top-right",
        autoClose: TOAST_DURATION,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        toastId: `service-${notification.actividadId}`,
        style: {
          minWidth: '380px',
          maxWidth: '420px',
          padding: '8px'
        }
      }
    );
  }, []);

  // Función para limpiar servicios notificados antiguos
  const cleanupNotifiedServices = useCallback((services: ActividadIndex[]) => {
    const cutoffTime = Date.now() - CLEANUP_THRESHOLD;
    
    services.forEach((actividad) => {
      if (actividad.fecha && new Date(actividad.fecha).getTime() < cutoffTime) {
        notifiedServices.current.delete(actividad.id);
      }
    });
  }, []);

  // Función principal para verificar servicios
  const checkForUpcomingServices = useCallback(async () => {
    if (!isLoggedIn || !usuario) return;

    try {
      const scheduledServices = await fetchUserScheduledServices();
      if (!scheduledServices.length) return;
      
      scheduledServices.forEach((actividad) => {
        if (!actividad.fecha || !actividad.servicio || !actividad.tipo_servicio) return;
        if (notifiedServices.current.has(actividad.id)) return;
        if (!shouldNotify(actividad.fecha)) return;
        
        const { clienteNombre, domicilio } = getClientInfo(actividad);
        const tecnico = `${actividad.usuario.nombre || ''} ${actividad.usuario.apellido_materno || ''}`.trim();
        
        console.log(`📋 Activity data:`, {
          actividadId: actividad.id,
          clienteNombre,
          domicilio,
          tipoServicio: actividad.tipo_servicio?.nombre || 'Sin tipo',
          fecha: actividad.fecha,
          hasSucursal: !!actividad.servicio.sucursal,
          hasCliente: !!actividad.servicio.cliente,
          tecnico
        });
        
        const notification: NotificationData = {
          actividadId: actividad.id,
          clienteNombre,
          tipoServicio: actividad.tipo_servicio.nombre || 'Servicio sin tipo',
          fecha: actividad.fecha,
          domicilio
        };
        
        showServiceNotification(notification);
        notifiedServices.current.add(actividad.id);
        
        console.log(`🔔 Notification sent for service ${actividad.id}`);
      });
      
      cleanupNotifiedServices(scheduledServices);
    } catch (error) {
      console.error('Error checking upcoming services:', error);
    }
  }, [isLoggedIn, usuario, fetchUserScheduledServices, shouldNotify, getClientInfo, showServiceNotification, cleanupNotifiedServices]);

  // Efecto para iniciar/detener el monitoreo
  useEffect(() => {
    if (!isLoggedIn || !usuario) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    checkForUpcomingServices();
    intervalRef.current = setInterval(checkForUpcomingServices, CHECK_INTERVAL);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isLoggedIn, usuario, checkForUpcomingServices]);

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      notifiedServices.current.clear();
    };
  }, []);

  return {
    checkForUpcomingServices,
    getNotifiedServicesCount: () => notifiedServices.current.size
  };
};

export default useServiceNotifications;
